import { useState } from "react";
import { Btn } from "../UI";
import { LAYOUTS, PAPERS } from "../../lib/rxPrint";
import { Printer, X, Ban } from "lucide-react";

const KEY = "rx_print_opts";
const DEFAULTS = { layout: "combined", paper: "full", header: true, npi: true, dea: false };
const load = () => { try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return DEFAULTS; } };

function Choice({ name, value, options, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(options).map(([k, label]) => (
        <label key={k} data-testid={`rx-print-${name}-${k}`} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-sm transition-colors ${value === k ? "border-[#4E342E] bg-[#F5EFE6] font-semibold text-[#3E2723]" : "border-[#E5DFD3] text-[#5D4037] hover:bg-[#FDFCF8]"}`}>
          <input type="radio" name={name} checked={value === k} onChange={() => onChange(k)} className="sr-only" />{label}
        </label>
      ))}
    </div>
  );
}

/** Pick the print layout, paper size, and which identifiers show on the script. */
export function RxPrintDialog({ open, count, voided, onClose, onPrint }) {
  const [o, setO] = useState(load);
  if (!open) return null;
  const set = (patch) => setO((s) => ({ ...s, ...patch }));
  const go = () => { localStorage.setItem(KEY, JSON.stringify(o)); onPrint(o); };
  const toggles = [["header", "Show clinic letterhead (logo, address, phone, fax)"], ["npi", "Show NPI numbers"], ["dea", "Show prescriber DEA number"]];
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#3E2723]/40 p-4 backdrop-blur-sm" data-testid="rx-print-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between"><div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">Print {count} medication{count === 1 ? "" : "s"}</h3>
          <p className="text-xs text-[#7B6157]">Every script carries the e-signature, a security footer, and page numbers.</p></div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="rx-print-close" className="rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={16} /></button></div>
        {voided && <p className="mb-3 flex items-center gap-1.5 rounded-xl bg-[#FFEBEE] px-3 py-2 text-xs font-semibold text-[#B71C1C]" data-testid="rx-print-void-note"><Ban size={12} /> This prescription is voided. Every page prints with a VOID watermark.</p>}
        <div className="space-y-4">
          <div><div className="mb-1.5 text-[10px] font-bold uppercase text-[#7B6157]">Layout</div><Choice name="layout" value={o.layout} options={LAYOUTS} onChange={(v) => set({ layout: v })} /></div>
          <div><div className="mb-1.5 text-[10px] font-bold uppercase text-[#7B6157]">Paper</div><Choice name="paper" value={o.paper} options={PAPERS} onChange={(v) => set({ paper: v })} /></div>
          <div className="space-y-2">{toggles.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-[#5D4037]"><input type="checkbox" data-testid={`rx-print-show-${k}`} checked={!!o[k]} onChange={(e) => set({ [k]: e.target.checked })} className="h-4 w-4 accent-[#4E342E]" /> {label}</label>
          ))}</div>
        </div>
        <div className="mt-6 flex justify-end gap-2"><Btn variant="ghost" data-testid="rx-print-cancel" onClick={onClose}>Cancel</Btn><Btn data-testid="rx-print-go" onClick={go}><Printer size={14} /> Print</Btn></div>
      </div>
    </div>
  );
}
